import { AppTheme } from '@backstage/core-plugin-api';
import {
  createUnifiedTheme,
  themes,
  UnifiedThemeProvider,
} from '@backstage/theme';
import { alpha, Theme, ThemeOptions } from '@mui/material/styles';
import GlobalStyles from '@mui/material/GlobalStyles';
import WbSunny from '@mui/icons-material/WbSunny';
import Brightness2 from '@mui/icons-material/Brightness2';
import { createMeltwaterPalette } from './palette';
import {
  styledBackstageContent,
  styledBackstageContentHeader,
  styledBackstageDependencyGraphNode,
  styledBackstageHeader,
  styledBackstageHeaderIconLinkRow,
  styledBackstageHeaderTabs,
  styledBackstageIconLinkVertical,
  styledBackstageInfoCard,
  styledBackstageItemCardHeader,
  styledBackstagePage,
  styledBackstageSelect,
  styledBackstageSidebar,
  styledBackstageSidebarDivider,
  styledBackstageSidebarItem,
  styledBackstageSidebarPage,
  styledBackstageCardTab,
  styledBackstageTabbedCard,
  styledBackstageTabbedCardBoldHeader,
  styledBackstageTableFiltersContainer,
  styledBackstageTableHeader,
  styledBackstageTableToolbar,
  styledBackstageTable,
  styledBackstageWarningPanel,
  styledMuiAlert,
  styledMuiBadge,
  styledMuiBreadcrumbs,
  styledMuiButton,
  styledMuiButtonBase,
  styledMuiCardContent,
  styledMuiCard,
  styledMuiChip,
  styledMuiDivider,
  styledMuiInputBase,
  styledMuiLinearProgress,
  styledMuiLink,
  styledMuiListItem,
  styledMuiListItemIcon,
  styledMuiListItemText,
  styledMuiMenuItem,
  styledMuiOutlinedInput,
  styledMuiPaper,
  styledMuiPopover,
  styledMuiSelect,
  styledMuiTab,
  styledMuiTableCell,
  styledMuiTabs,
  styledMuiTypography,
} from './components';
import {
  getPageThemes,
  getSurfaceBorder,
  getSurfaceColor,
  getThemeTokens,
  meltwaterTypography,
  radii,
  shellMetrics,
} from './tokens';

const sampleTheme = themes.light.getTheme('v5');

if (!sampleTheme) {
  throw new Error('Missing Backstage v5 light theme');
}

const createStyleOverrides = (
  factory: (theme: Theme) => Record<string, unknown> | undefined,
) => {
  const slots = Object.keys(factory(sampleTheme as Theme) ?? {});

  return Object.fromEntries(
    slots.map(slot => [
      slot,
      ({ theme }: { theme: Theme }) => (factory(theme) ?? {})[slot],
    ]),
  ) as any;
};

const gridSpacingValues = [1, 2, 3, 4] as const;
const docsFilterPanel =
  '.MuiGrid-grid-lg-2:has([class*="CatalogReactUserListPicker"])';
const docsFilterContainer =
  '.MuiGrid-container:has(> .MuiGrid-grid-lg-2 [class*="CatalogReactUserListPicker"])';

const getGlobalStyles = (theme: Theme) => {
  const tokens = getThemeTokens(theme);
  const filterColumnWidth = 260;

  return {
    '.swagger-ui': {
      color: tokens.textPrimary,
      fontFamily: theme.typography.fontFamily,
      fontSize: theme.typography.body2.fontSize,
      lineHeight: theme.typography.body2.lineHeight,
    },
    '.swagger-ui .wrapper': {
      maxWidth: 'none',
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
      [theme.breakpoints.down('sm')]: {
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
      },
    },
    '.swagger-ui .info': {
      margin: theme.spacing(4, 0),
    },
    '.swagger-ui .info .title': {
      color: tokens.textPrimary,
      fontSize: theme.typography.h4.fontSize,
      lineHeight: theme.typography.h4.lineHeight,
      marginBottom: theme.spacing(1.5),
    },
    '.swagger-ui .info .description, .swagger-ui .info p': {
      color: tokens.textSecondary,
      fontSize: theme.typography.body1.fontSize,
      lineHeight: theme.typography.body1.lineHeight,
    },
    '.swagger-ui .scheme-container': {
      backgroundColor: getSurfaceColor(theme, 'subtle'),
      border: `1px solid ${getSurfaceBorder(theme)}`,
      borderRadius: radii.lg,
      boxShadow: 'none',
      marginBottom: theme.spacing(3),
      padding: theme.spacing(2.5, 0),
    },
    '.swagger-ui select': {
      borderColor: getSurfaceBorder(theme, 'strong'),
      borderRadius: radii.sm,
      color: tokens.textPrimary,
      minHeight: theme.spacing(4),
    },
    '.swagger-ui .btn.authorize': {
      borderRadius: radii.sm,
      boxShadow: 'none',
      minHeight: theme.spacing(4),
    },
    '.swagger-ui .opblock-tag': {
      color: tokens.textPrimary,
      fontSize: theme.typography.h6.fontSize,
      lineHeight: theme.typography.h6.lineHeight,
      margin: theme.spacing(1.5, 0, 1),
      padding: theme.spacing(1.25, 2, 1.25, 1.25),
    },
    '.swagger-ui .opblock': {
      borderRadius: radii.sm,
      boxShadow: 'none',
      marginBottom: theme.spacing(1.5),
      overflow: 'hidden',
    },
    '.swagger-ui .opblock .opblock-summary': {
      minHeight: theme.spacing(5),
    },
    '.swagger-ui .opblock .opblock-summary-method': {
      borderRadius: radii.sm,
      minWidth: theme.spacing(9),
    },
    '[data-sidebar-state] [data-testid="sidebar-root"] > div': {
      width: `${shellMetrics.railClosedWidth}px !important`,
    },
    '[data-sidebar-state="pinned"] [data-testid="sidebar-root"] > div, [data-sidebar-state="unpinned"] [data-testid="sidebar-root"]:hover > div, [data-sidebar-state="unpinned"] [data-testid="sidebar-root"]:focus-within > div':
      {
        width: `${shellMetrics.railOpenWidth}px !important`,
      },
    '[class*="BackstageSidebar-drawer"]': {
      width: `${shellMetrics.railClosedWidth}px`,
    },
    '[class*="BackstageSidebar-drawer"][class*="BackstageSidebar-drawerOpen"]':
      {
        width: `${shellMetrics.railOpenWidth}px !important`,
      },
    '[class*="v5-MuiGrid-container"] > [class*="v5-MuiGrid-item"]': {
      minWidth: 0,
    },
    ...Object.fromEntries(
      gridSpacingValues.map(spacing => [
        `[class*="v5-MuiGrid-container"][class*="v5-MuiGrid-spacing-xs-${spacing}"] > [class*="v5-MuiGrid-item"]`,
        {
          paddingLeft: theme.spacing(spacing),
          paddingTop: theme.spacing(spacing),
        },
      ]),
    ),
    [docsFilterContainer]: {
      alignItems: 'start',
      display: 'grid',
      gap: theme.spacing(2),
      gridTemplateColumns: `${filterColumnWidth}px minmax(0, 1fr)`,
    },
    [docsFilterPanel]: {
      backgroundColor: getSurfaceColor(theme, 'subtle'),
      border: `1px solid ${getSurfaceBorder(theme)}`,
      borderRadius: `${radii.xl}px`,
      maxWidth: 'unset',
      padding: theme.spacing(2.25),
    },
    [`${docsFilterContainer} > .MuiGrid-grid-lg-10`]: {
      maxWidth: 'unset',
      minWidth: 0,
    },
    [`${docsFilterPanel} [class*="CatalogReactUserListPicker-root"]`]: {
      backgroundColor: getSurfaceColor(theme, 'raised'),
      border: `1px solid ${getSurfaceBorder(theme)}`,
      borderRadius: `${radii.md}px`,
      boxShadow: 'none',
      margin: 0,
      overflow: 'hidden',
    },
    [`${docsFilterPanel} [class*="CatalogReactUserListPicker-title"]`]: {
      color: tokens.textPrimary,
      fontSize: 13,
      fontWeight: 700,
      margin: theme.spacing(1.25, 1.5, 0.5),
      textTransform: 'none' as const,
    },
    [`${docsFilterPanel} [class*="CatalogReactUserListPicker-groupWrapper"]`]: {
      boxShadow: 'none',
      margin: theme.spacing(0, 0.5, 0.5),
    },
    [`${docsFilterPanel} [class*="CatalogReactUserListPicker-menuItem"]`]: {
      borderRadius: `${radii.sm}px`,
      minHeight: theme.spacing(5),
    },
    [`${docsFilterPanel} [class*="BackstageAutocomplete-label"]`]: {
      color: tokens.textPrimary,
      display: 'flex',
      flexDirection: 'column' as const,
      fontSize: 14,
      fontWeight: 700,
      gap: theme.spacing(1),
      margin: 0,
      width: '100%',
    },
    [`${docsFilterPanel} [class*="BackstageAutocomplete-label"] > span:first-of-type`]:
      {
        color: tokens.textPrimary,
        fontSize: 14,
        fontWeight: 700,
        position: 'static' as const,
      },
    [`${docsFilterPanel} [class*="BackstageAutocompleteBase-inputRoot"]`]: {
      backgroundColor: getSurfaceColor(theme, 'raised'),
      border: `1px solid ${getSurfaceBorder(theme)}`,
      borderRadius: `${radii.md}px`,
      marginTop: 0,
      paddingLeft: theme.spacing(1.25),
    },
    [`${docsFilterPanel} [class*="BackstageAutocompleteBase-inputRoot"] fieldset`]:
      {
        borderColor: getSurfaceBorder(theme),
      },
    '[data-testid="mobile-sidebar-root"]': {
      backgroundColor: alpha(theme.palette.background.paper, 0.98),
      borderTop: `1px solid ${getSurfaceBorder(theme)}`,
      boxShadow: `0 -12px 28px ${alpha(theme.palette.common.black, 0.12)}`,
      boxSizing: 'border-box',
      height: `calc(${shellMetrics.mobileSidebarHeight}px + env(safe-area-inset-bottom))`,
      minHeight: shellMetrics.mobileSidebarHeight,
      paddingBottom: 'env(safe-area-inset-bottom)',
    },
    '[data-testid="mobile-sidebar-root"] [class*="MuiBottomNavigationAction-root"]':
      {
        margin: 0,
        minWidth: 64,
      },
  };
};

const components: ThemeOptions['components'] = {
  BackstageContent: {
    styleOverrides: createStyleOverrides(styledBackstageContent),
  },
  BackstageContentHeader: {
    styleOverrides: createStyleOverrides(styledBackstageContentHeader),
  },
  BackstageDependencyGraphNode: {
    styleOverrides: createStyleOverrides(styledBackstageDependencyGraphNode),
  },
  BackstageHeader: {
    styleOverrides: createStyleOverrides(styledBackstageHeader),
  },
  BackstageHeaderIconLinkRow: {
    styleOverrides: createStyleOverrides(styledBackstageHeaderIconLinkRow),
  },
  BackstageHeaderTabs: {
    styleOverrides: createStyleOverrides(styledBackstageHeaderTabs),
  },
  BackstageIconLinkVertical: {
    styleOverrides: createStyleOverrides(styledBackstageIconLinkVertical),
  },
  BackstageInfoCard: {
    styleOverrides: createStyleOverrides(styledBackstageInfoCard),
  },
  BackstageItemCardHeader: {
    styleOverrides: createStyleOverrides(styledBackstageItemCardHeader),
  },
  BackstagePage: {
    styleOverrides: createStyleOverrides(styledBackstagePage),
  },
  BackstageSelect: {
    styleOverrides: createStyleOverrides(styledBackstageSelect),
  },
  BackstageSidebar: {
    styleOverrides: createStyleOverrides(styledBackstageSidebar),
  },
  BackstageSidebarDivider: {
    styleOverrides: createStyleOverrides(styledBackstageSidebarDivider),
  },
  BackstageSidebarItem: {
    styleOverrides: createStyleOverrides(styledBackstageSidebarItem),
  },
  BackstageSidebarPage: {
    styleOverrides: createStyleOverrides(styledBackstageSidebarPage),
  },
  BackstageCardTab: {
    styleOverrides: createStyleOverrides(styledBackstageCardTab),
  },
  BackstageTabbedCard: {
    styleOverrides: createStyleOverrides(styledBackstageTabbedCard),
  },
  BackstageTabbedCardBoldHeader: {
    styleOverrides: createStyleOverrides(styledBackstageTabbedCardBoldHeader),
  },
  BackstageTable: {
    styleOverrides: createStyleOverrides(styledBackstageTable),
  },
  BackstageTableFiltersContainer: {
    styleOverrides: createStyleOverrides(styledBackstageTableFiltersContainer),
  },
  BackstageTableHeader: {
    styleOverrides: createStyleOverrides(styledBackstageTableHeader),
  },
  BackstageTableToolbar: {
    styleOverrides: createStyleOverrides(styledBackstageTableToolbar),
  },
  BackstageWarningPanel: {
    styleOverrides: createStyleOverrides(styledBackstageWarningPanel),
  },
  MuiAlert: {
    styleOverrides: createStyleOverrides(styledMuiAlert),
  },
  MuiBadge: {
    styleOverrides: createStyleOverrides(styledMuiBadge),
  },
  MuiBreadcrumbs: {
    styleOverrides: createStyleOverrides(styledMuiBreadcrumbs),
  },
  MuiButton: {
    defaultProps: {
      disableElevation: true,
    },
    styleOverrides: createStyleOverrides(styledMuiButton),
  },
  MuiButtonBase: {
    defaultProps: {
      disableRipple: true,
    },
    styleOverrides: createStyleOverrides(styledMuiButtonBase),
  },
  MuiCard: {
    styleOverrides: createStyleOverrides(styledMuiCard),
  },
  MuiCardContent: {
    styleOverrides: createStyleOverrides(styledMuiCardContent),
  },
  MuiChip: {
    styleOverrides: createStyleOverrides(styledMuiChip),
  },
  MuiDivider: {
    styleOverrides: createStyleOverrides(styledMuiDivider),
  },
  MuiInputBase: {
    styleOverrides: createStyleOverrides(styledMuiInputBase),
  },
  MuiLinearProgress: {
    styleOverrides: createStyleOverrides(styledMuiLinearProgress),
  },
  MuiLink: {
    styleOverrides: createStyleOverrides(styledMuiLink),
  },
  MuiListItem: {
    styleOverrides: createStyleOverrides(styledMuiListItem),
  },
  MuiListItemIcon: {
    styleOverrides: createStyleOverrides(styledMuiListItemIcon),
  },
  MuiListItemText: {
    styleOverrides: createStyleOverrides(styledMuiListItemText),
  },
  MuiMenuItem: {
    styleOverrides: createStyleOverrides(styledMuiMenuItem),
  },
  MuiOutlinedInput: {
    styleOverrides: createStyleOverrides(styledMuiOutlinedInput),
  },
  MuiPaper: {
    defaultProps: {
      elevation: 0,
    },
    styleOverrides: createStyleOverrides(styledMuiPaper),
  },
  MuiPopover: {
    styleOverrides: createStyleOverrides(styledMuiPopover),
  },
  MuiSelect: {
    styleOverrides: createStyleOverrides(styledMuiSelect),
  },
  MuiTab: {
    styleOverrides: createStyleOverrides(styledMuiTab),
  },
  MuiTableCell: {
    styleOverrides: createStyleOverrides(styledMuiTableCell),
  },
  MuiTabs: {
    styleOverrides: createStyleOverrides(styledMuiTabs),
  },
  MuiTypography: {
    styleOverrides: createStyleOverrides(styledMuiTypography),
  },
};

export const meltwaterThemes: AppTheme[] = [
  {
    id: 'light',
    title: 'Light',
    variant: 'light',
    icon: <WbSunny />,
    Provider: ({ children }) => (
      <UnifiedThemeProvider
        theme={createUnifiedTheme({
          components,
          defaultPageTheme: 'home',
          fontFamily: meltwaterTypography.fontFamily,
          htmlFontSize: meltwaterTypography.htmlFontSize,
          pageTheme: getPageThemes('light'),
          palette: createMeltwaterPalette('light'),
          typography: meltwaterTypography,
        })}
      >
        <GlobalStyles styles={getGlobalStyles} />
        {children}
      </UnifiedThemeProvider>
    ),
  },
  {
    id: 'dark',
    title: 'Dark',
    variant: 'dark',
    icon: <Brightness2 />,
    Provider: ({ children }) => (
      <UnifiedThemeProvider
        theme={createUnifiedTheme({
          components,
          defaultPageTheme: 'home',
          fontFamily: meltwaterTypography.fontFamily,
          htmlFontSize: meltwaterTypography.htmlFontSize,
          pageTheme: getPageThemes('dark'),
          palette: createMeltwaterPalette('dark'),
          typography: meltwaterTypography,
        })}
      >
        <GlobalStyles styles={getGlobalStyles} />
        {children}
      </UnifiedThemeProvider>
    ),
  },
];
